import { useEffect, useState, useCallback } from "react";
import api from "../api/axios";
import AppShell from "../components/AppShell";
import CreateAdminModal from "../components/CreateAdminModal";
import PermissionSwitchboard from "../components/PermissionSwitchboard";
import RowIconAction from "../components/RowIconAction";
import { Ban, CircleCheck, Trash2 } from "lucide-react";

export default function AdminManagement() {
  const [admins, setAdmins] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [adminsRes, permsRes] = await Promise.all([
        api.get("/admins"),
        api.get("/admins/permissions"),
      ]);
      setAdmins(adminsRes.data.admins);
      setAllPermissions(permsRes.data.permissions);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load admins");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (payload) => {
    const { data } = await api.post("/admins", payload);
    setAdmins((prev) => [data.admin, ...prev]);
    setShowCreate(false);
  };

  const handlePermissionChange = async (adminId, newPermissions) => {
    setSavingId(adminId);
    // Optimistic update
    setAdmins((prev) =>
      prev.map((a) => (a.id === adminId ? { ...a, permissions: newPermissions } : a))
    );
    try {
      await api.put(`/admins/${adminId}/permissions`, { permissions: newPermissions });
    } catch (err) {
      setError(err.response?.data?.message || "Could not update permissions");
      load();
    } finally {
      setSavingId(null);
    }
  };

  const toggleStatus = async (admin) => {
    const nextStatus = admin.status === "active" ? "suspended" : "active";
    try {
      const { data } = await api.put(`/admins/${admin.id}`, { status: nextStatus });
      setAdmins((prev) => prev.map((a) => (a.id === admin.id ? data.admin : a)));
    } catch (err) {
      setError(err.response?.data?.message || "Could not update status");
    }
  };

  const removeAdmin = async (admin) => {
    if (!window.confirm(`Remove ${admin.name}? This can't be undone.`)) return;
    try {
      await api.delete(`/admins/${admin.id}`);
      setAdmins((prev) => prev.filter((a) => a.id !== admin.id));
    } catch (err) {
      setError(err.response?.data?.message || "Could not remove admin");
    }
  };

  return (
    <AppShell>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Admin Management</h1>
          <p style={styles.subtitle}>
            Add sub admins and control exactly which features each one can use.
          </p>
        </div>
        <button style={styles.addBtn} onClick={() => setShowCreate(true)}>
          + New sub admin
        </button>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {loading ? (
        <div style={styles.muted}>Loading…</div>
      ) : (
        <div className="admin-list" style={styles.list}>
          {admins.map((admin) => (
            <div key={admin.id} className="admin-record" style={styles.card}>
              <div className="admin-record-header" style={styles.cardHeader} onClick={() => setExpandedId(expandedId === admin.id ? null : admin.id)}>
                <div>
                  <div style={styles.cardName}>
                    {admin.name}{" "}
                    {admin.role === "superadmin" && <span style={styles.badgeSuper}>SUPER ADMIN</span>}
                    {admin.status === "suspended" && <span style={styles.badgeSuspended}>SUSPENDED</span>}
                  </div>
                  <div style={styles.cardEmail}>{admin.email}</div>
                </div>
                {admin.role !== "superadmin" && (
                  <div className="row-actions" style={styles.cardActions} onClick={(e) => e.stopPropagation()}>
                    <RowIconAction label={admin.status === "active" ? `Suspend ${admin.name}` : `Reactivate ${admin.name}`} icon={admin.status === "active" ? Ban : CircleCheck} onClick={() => toggleStatus(admin)} />
                    <RowIconAction label={`Remove ${admin.name}`} icon={Trash2} onClick={() => removeAdmin(admin)} danger />
                  </div>
                )}
              </div>

              {admin.role !== "superadmin" && expandedId === admin.id && (
                <div style={styles.expanded}>
                  {savingId === admin.id && <div style={styles.savingNote}>Saving…</div>}
                  <PermissionSwitchboard
                    allPermissions={allPermissions}
                    selected={admin.permissions}
                    onChange={(next) => handlePermissionChange(admin.id, next)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateAdminModal
          allPermissions={allPermissions}
          onClose={() => setShowCreate(false)}
          onCreate={handleCreate}
        />
      )}
    </AppShell>
  );
}

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 },
  title: { fontFamily: "var(--font-display)", fontSize: 28, margin: 0 },
  subtitle: { color: "var(--slate-500)", fontSize: 14, marginTop: 6 },
  addBtn: {
    background: "var(--ink)",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    padding: "10px 18px",
    fontSize: 14,
    fontWeight: 600,
  },
  errorBanner: {
    background: "rgba(217,99,78,0.1)",
    border: "1px solid var(--danger)",
    color: "#a8402c",
    padding: "10px 14px",
    borderRadius: 4,
    marginBottom: 16,
    fontSize: 13,
  },
  muted: { color: "var(--slate-500)" },
  list: { display: "flex", flexDirection: "column", gap: 12 },
  card: {
    background: "#fff",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius)",
    overflow: "hidden",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 18px",
    cursor: "pointer",
  },
  cardName: { fontSize: 15, fontWeight: 600 },
  cardEmail: { fontSize: 13, color: "var(--slate-500)", marginTop: 2 },
  cardActions: { display: "flex", gap: 4 },
  badgeSuper: {
    marginLeft: 8,
    fontSize: 10,
    background: "var(--signal)",
    color: "#1B1300",
    padding: "2px 6px",
    borderRadius: 3,
    fontWeight: 700,
  },
  badgeSuspended: {
    marginLeft: 8,
    fontSize: 10,
    background: "rgba(217,99,78,0.15)",
    color: "var(--danger)",
    padding: "2px 6px",
    borderRadius: 3,
    fontWeight: 700,
  },
  expanded: { borderTop: "1px solid var(--line)", padding: "16px 18px" },
  savingNote: { fontSize: 12, color: "var(--slate-500)", marginBottom: 8 },
};
